import React, { useEffect, useRef, useState } from 'react';

import { getFactionTextureUrl, getTextureUrl }
    from '../../Common/TextureTools';
import { FACTION_BS_VARIANT, FACTION_CSS_COLORS }
    from '../../../../Constants';
import { getTextureName } from '../../../../TextureNames';

import './ActivityFeed.css';

// ActivityFeed — calm, icon-led, vertically-stacked activity feed.
//
// Stands up alongside the legacy <TranscriptTicker/> carousel ticker
// (see CC_ACTIVITY_V2_FEED). No carousel, no autoplay: the newest event
// fades in at the top with a faction-tinted glow; every row carries its
// actor's faction color (left border, subtle background, icon chip
// backing). Rows persist and the list scrolls, so there is no age-based
// dim ramp. Icons are the primary language; the caption row underneath
// is composed client-side from display_name + the canonical TName table,
// each part colored by faction / Rule of 6 (see
// captionSegmentsForTranscript).
//
// Two sources are merged into one feed by arrival order (see the
// subscription effect): icon-led game transcripts and wire-only prose
// 'message-event' rows. Three header toggles, all default ON:
//   - Actions  — show / hide the transcript source (filtered at render).
//   - Messages — show / hide the message source (filtered at render).
//   - CC       — show / hide the composed caption layer under transcript
//                rows; message-payload rows (chat / system / error)
//                ignore it and always show their text.
// The source toggles filter at render time — hidden entries stay in
// state and reappear the instant a toggle flips back ON.

const ACTIONS_LS_KEY = 'hsh.activityFeed.actionsOn';
const MESSAGES_LS_KEY = 'hsh.activityFeed.messagesOn';
// CC (captions) toggle — shows / hides the composed caption layer under
// icon-led transcript rows. Meaningful again now the caption is composed
// client-side (see captionSegmentsForTranscript); rows whose message IS
// the payload (chat / system / error) ignore it and always show.
const CAPTIONS_LS_KEY = 'hsh.activityFeed.captionsOn';
// Bounded memory growth, far beyond any single in-game session's
// interesting history depth, while leaving headroom for long games.
const MAX_ENTRIES = 100;
const MAX_OUTCOMES = 4;
const TICK_INTERVAL_MS = 10_000;
// Faction tint alpha for the row background — stronger for the newest
// row, subtle for the rest.
const NEWEST_TINT_ALPHA = 0.20;
const ROW_TINT_ALPHA = 0.08;
// Faction tint alpha behind each icon chip.
const CHIP_TINT_ALPHA = 0.35;

// TranscriptType ids — mirror transcripts/models.py TranscriptType.
const ACTION_ID = 1;
const SCORE_ID = 2;
const CAMPAIGN_ID = 3;
const AI_CHATTER_ID = 4;
const CHAT_ID = 5;
const SYSTEM_ID = 6;
const ERROR_ID = 7;

// Type id → modifier-class suffix. CSS hangs off
// `activity-feed-row--{typeName}` so each type can be styled later.
const TRANSCRIPT_TYPE_NAMES = {
    [ACTION_ID]: 'action',
    [SCORE_ID]: 'score',
    [CAMPAIGN_ID]: 'campaign',
    [AI_CHATTER_ID]: 'aiChatter',
    [CHAT_ID]: 'chat',
    [SYSTEM_ID]: 'system',
    [ERROR_ID]: 'error',
};

// Categorical prefixes stripped from a TName so the caption reads
// naturally (Research / Casino / Deep) instead of carrying boilerplate
// ("Action Room" / "Amenity Room"). Ordered longest-first so a specific
// prefix wins over a shorter one it contains (e.g. "Action Room " must
// be tried before "Action "). Names with no listed prefix (e.g. "Snohe")
// pass through verbatim.
const TEXTURE_NAME_PREFIXES_TO_STRIP = [
    'Upgraded Action Room ',
    'Upgraded Amenity Room ',
    'Upgraded Bed Room ',
    'Upgraded Lodging ',
    'Faction Action ',
    'Action Room ',
    'Amenity Room ',
    'Bed Room ',
    'Lodging ',
    'Action Icon ',
    'Action ',
];

// Read a persisted boolean source-filter flag. Defaults to ON when unset
// or unreadable — both sources show until the player opts one out.
function readFlag(key) {
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return true;
        return raw === 'true';
    } catch (_err) {
        return true;
    }
}

// Player-facing name for a subject TName, with the categorical prefix
// stripped. Returns null for a falsy or unknown id. Exported for unit
// tests (prefix-stripping is pure string logic).
export function textureName(textureId) {
    if (!textureId) return null;
    const raw = getTextureName(textureId);
    if (!raw) return null;
    for (const prefix of TEXTURE_NAME_PREFIXES_TO_STRIP) {
        if (raw.startsWith(prefix)) return raw.slice(prefix.length);
    }
    return raw;
}

// Rule of 6 — each action's color is the faction whose strength it is.
// Mirrors the manual's six-of-everything strength wheel:
//   Research      → DEEP's strength      → DEEP color
//   Lodging       → STEM's strength      → STEM color
//   Staff         → HEX's strength       → HEX color
//   Renovation    → DREAD's strength     → DREAD color
//   Advertisement → HONORED's strength   → HONORED color
//   Ghost         → SEED's strength      → SEED color
// Keyed on Texture.ACTIONS values (71-76) from textures/models.py.
const ACTION_VERB_COLORS = {
    71: FACTION_CSS_COLORS[1],  // Research      → DEEP
    72: FACTION_CSS_COLORS[2],  // Lodging       → STEM
    73: FACTION_CSS_COLORS[3],  // Staff         → HEX
    74: FACTION_CSS_COLORS[4],  // Renovation    → DREAD
    75: FACTION_CSS_COLORS[5],  // Advertisement → HONORED
    76: FACTION_CSS_COLORS[6],  // Ghost         → SEED
};

// Rule-of-6 color for an action-verb subject (TName 71-76). null means
// "no color override — render in default white." Non-action subjects
// (amenity rooms / cards / snohe denominations) return null today;
// future passes may extend the table. Exported for unit tests.
export function subjectColor(textureId) {
    return ACTION_VERB_COLORS[textureId] || null;
}

// Compose the caption as an array of {text, color} segments so each part
// renders in its own color. Honors backend-authored prose
// (transcript.message) by returning a single neutral segment — backend
// owns the copy and color is its choice. For composed rows, the actor
// wears its faction color, the subject wears its Rule-of-6 verb color
// (when the subject is an action-icon TName), the target wears its
// faction color. `color: null` means "render in default white." Missing
// fields are simply omitted — no empty-string segments. Exported for
// unit tests.
export function captionSegmentsForTranscript(transcript) {
    if (transcript.message) {
        return [ { text: transcript.message, color: null } ];
    }
    const segments = [];
    const actor = transcript.game_player;
    if (actor && actor.display_name) {
        segments.push({
            text: actor.display_name,
            color: actor.faction
                ? FACTION_CSS_COLORS[actor.faction] : null,
        });
    }
    const subjectName = textureName(transcript.subject);
    if (subjectName) {
        segments.push({
            text: subjectName,
            color: subjectColor(transcript.subject),
        });
    }
    const target = transcript.to_game_player;
    if (target && target.display_name) {
        segments.push({
            text: target.display_name,
            color: target.faction
                ? FACTION_CSS_COLORS[target.faction] : null,
        });
    }
    return segments;
}

// Inline faction tint for a row — left border + subtle background.
// Applied to EVERY row with a known faction (not just the newest); the
// newest simply gets a stronger background alpha. Returns undefined for
// faction-less rows (system / error / message), which fall back to the
// neutral CSS border. var()-based so a real browser resolves the faction
// palette — jsdom's cssstyle drops var() values, so tests assert this
// builder directly rather than the rendered node's style.
export function factionRowStyle(factionId, isNewest) {
    const variant = factionId ? FACTION_BS_VARIANT[factionId] : null;
    if (!factionId || !variant) return undefined;
    const alpha = isNewest ? NEWEST_TINT_ALPHA : ROW_TINT_ALPHA;
    return {
        borderLeftColor: FACTION_CSS_COLORS[factionId],
        background: `rgba(var(--${variant}-rgb), ${alpha})`,
    };
}

// Inline faction tint backing for an icon chip. Genuinely dynamic
// (faction-driven), so it rides an inline style — the only inline style
// the guide permits. Exported for unit tests (see factionRowStyle on the
// jsdom var() caveat).
export function factionChipStyle(variant) {
    if (!variant) return undefined;
    return { background: `rgba(var(--${variant}-rgb), ${CHIP_TINT_ALPHA})` };
}

// Branch row shape off transcript.type. ACTION / CAMPAIGN / AI_CHATTER
// share the full actor→subject→target?→outcomes shape; SCORE drops the
// subject; CHAT is actor + message; SYSTEM / ERROR are message-only with
// no actor icon. `messageIsPayload` marks the rows whose caption text is
// the payload (chat / system / error) — those always show regardless of
// the CC toggle.
function getRowShape(typeId) {
    const typeName = TRANSCRIPT_TYPE_NAMES[typeId] || 'action';
    switch (typeId) {
        case CHAT_ID:
            return {
                typeName, showActor: true, showSubject: false,
                showTarget: false, showOutcomes: false,
                messageIsPayload: true,
            };
        case SYSTEM_ID:
        case ERROR_ID:
            return {
                typeName, showActor: false, showSubject: false,
                showTarget: false, showOutcomes: false,
                messageIsPayload: true,
            };
        case SCORE_ID:
            return {
                typeName, showActor: true, showSubject: false,
                showTarget: false, showOutcomes: true,
                messageIsPayload: false,
            };
        case ACTION_ID:
        case CAMPAIGN_ID:
        case AI_CHATTER_ID:
        default:
            return {
                typeName, showActor: true, showSubject: true,
                showTarget: true, showOutcomes: true,
                messageIsPayload: false,
            };
    }
}

// Relative time for the time column. 'now' for the just-arrived event,
// then M:SS counting up from receive time. Driven by the shared tick
// interval — `now` is a render input, not a clock read inside render.
function formatRelative(receivedAt, now) {
    const totalSec = Math.max(0, Math.floor((now - receivedAt) / 1000));
    if (totalSec < 1) return 'now';
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export default function ActivityFeed() {
    const [entries, setEntries] = useState([]);
    const [actionsOn, setActionsOn] = useState(() => readFlag(ACTIONS_LS_KEY));
    const [messagesOn, setMessagesOn] =
        useState(() => readFlag(MESSAGES_LS_KEY));
    const [captionsOn, setCaptionsOn] =
        useState(() => readFlag(CAPTIONS_LS_KEY));
    const [now, setNow] = useState(() => Date.now());
    // Monotonic id source for stable React keys. A new node mounts only
    // when its key is new, which is what scopes the fade-in animation to
    // the genuinely-new top row.
    const idRef = useRef(0);

    // Two in-stream sources merged into one feed by arrival order:
    //   - 'transcript-message-event' → DB-persisted game transcripts
    //     (actor / subject / outcomes). e.detail.package.data.transcript.
    //   - 'message-event' → wire-only prose (chat replies, seat-taken
    //     broadcasts, accepted-move strings). e.detail is a plain string
    //     (see api/PackageRouter.js: dispatch of package.message).
    //
    // Reload asymmetry (Michael's explicit call, see
    // CC_ACTIVITY_V2_MESSAGES): transcripts replay on rejoin via the
    // engine's `send_transcripts` dispatcher, so a reloaded feed rebuilds
    // its transcript history; message-event entries are wire-only and do
    // NOT replay, so the message stream restarts empty. Do not surface
    // this asymmetry in the UI.
    //
    // Both listeners live in ONE useEffect so their lifecycles are
    // obviously parallel — there is no path where one mounts without the
    // other. The event handlers are the data source — no polling.
    // setNow(receivedAt) keeps the newest row reading 'now' instead of
    // up-to-10s stale from the tick interval. The MAX_ENTRIES cap applies
    // to the underlying entries array; the per-source toggles filter the
    // displayed subset at render time, so hidden entries are never lost.
    useEffect(() => {
        function receiveTranscript(e) {
            const transcript =
                e && e.detail && e.detail.package
                && e.detail.package.data
                    ? e.detail.package.data.transcript
                    : null;
            if (!transcript) return;
            const receivedAt = Date.now();
            const id = idRef.current;
            idRef.current += 1;
            setNow(receivedAt);
            setEntries((prev) =>
                [ { id, kind: 'transcript', receivedAt, transcript },
                  ...prev ].slice(0, MAX_ENTRIES));
        }
        function receiveMessage(e) {
            const text = e && e.detail;
            if (typeof text !== 'string' || text.length === 0) return;
            const receivedAt = Date.now();
            const id = idRef.current;
            idRef.current += 1;
            setNow(receivedAt);
            setEntries((prev) =>
                [ { id, kind: 'message', receivedAt, message: text },
                  ...prev ].slice(0, MAX_ENTRIES));
        }
        window.addEventListener(
            'transcript-message-event', receiveTranscript);
        window.addEventListener('message-event', receiveMessage);
        return () => {
            window.removeEventListener(
                'transcript-message-event', receiveTranscript);
            window.removeEventListener('message-event', receiveMessage);
        };
    // Empty deps: listeners wire once for the component lifetime.
    }, []);

    // Re-render trigger for the relative time column. Purely a clock
    // tick — not a data fetch.
    useEffect(() => {
        const interval = setInterval(
            () => setNow(Date.now()), TICK_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    // Persist each source-filter flag whenever it flips. Two effects (one
    // per flag) keep each write keyed to its own dep cleanly.
    useEffect(() => {
        try {
            window.localStorage.setItem(
                ACTIONS_LS_KEY, actionsOn ? 'true' : 'false');
        } catch (_err) {
            // ignored — state still drives UI for this session.
        }
    }, [actionsOn]);

    useEffect(() => {
        try {
            window.localStorage.setItem(
                MESSAGES_LS_KEY, messagesOn ? 'true' : 'false');
        } catch (_err) {
            // ignored — state still drives UI for this session.
        }
    }, [messagesOn]);

    // Persist the CC (captions) flag whenever it flips.
    useEffect(() => {
        try {
            window.localStorage.setItem(
                CAPTIONS_LS_KEY, captionsOn ? 'true' : 'false');
        } catch (_err) {
            // ignored — state still drives UI for this session.
        }
    }, [captionsOn]);

    // Filter at render time so flipping a toggle back ON re-reveals the
    // hidden entries immediately (no data loss). The cap already bounded
    // the underlying entries array.
    const visible = entries.filter((entry) => {
        if (entry.kind === 'transcript') return actionsOn;
        if (entry.kind === 'message') return messagesOn;
        return true;
    });

    return (
        <div className={'activity-feed'} data-testid={'activity-feed'}>
            <div className={'activity-feed-header'}>
                <button type={'button'}
                        className={'activity-feed-source-toggle'
                            + (actionsOn
                                ? ''
                                : ' activity-feed-source-toggle--off')}
                        aria-label={'Toggle action transcripts'}
                        aria-pressed={actionsOn}
                        data-testid={'activity-feed-actions-toggle'}
                        onClick={() => setActionsOn((v) => !v)}>
                    Actions
                </button>
                <button type={'button'}
                        className={'activity-feed-source-toggle'
                            + (messagesOn
                                ? ''
                                : ' activity-feed-source-toggle--off')}
                        aria-label={'Toggle messages'}
                        aria-pressed={messagesOn}
                        data-testid={'activity-feed-messages-toggle'}
                        onClick={() => setMessagesOn((v) => !v)}>
                    Messages
                </button>
                <button type={'button'}
                        className={'activity-feed-source-toggle'
                            + (captionsOn
                                ? ''
                                : ' activity-feed-source-toggle--off')}
                        aria-label={'Toggle captions'}
                        aria-pressed={captionsOn}
                        data-testid={'activity-feed-caption-toggle'}
                        onClick={() => setCaptionsOn((v) => !v)}>
                    <span aria-hidden={'true'}>CC</span>
                </button>
            </div>

            <div className={'activity-feed-list'}
                 tabIndex={0}
                 aria-label={'Activity history'}
                 data-testid={'activity-feed-list'}>
                {visible.map((entry, index) => {
                    const isNewest = index === 0;

                    // Wire-only prose row: no actor icon, no faction tint,
                    // message text IS the payload. Distinct `--message`
                    // modifier hook; no CSS rule required (matches the
                    // existing no-rule per-type modifier convention).
                    if (entry.kind === 'message') {
                        const msgRowClass = [
                            'activity-feed-row',
                            'activity-feed-row--message',
                            isNewest ? 'activity-feed-row--newest' : '',
                        ].filter(Boolean).join(' ');
                        return (
                            <div key={entry.id}
                                 className={msgRowClass}
                                 data-testid={'activity-feed-row'}>
                                <div className={'activity-feed-row__main'}>
                                    <span className={'activity-feed-time'}
                                          data-testid={
                                              'activity-feed-time'}>
                                        {formatRelative(
                                            entry.receivedAt, now)}
                                    </span>
                                </div>
                                <div className={'activity-feed-caption'}
                                     data-testid={'activity-feed-message'}>
                                    {entry.message}
                                </div>
                            </div>
                        );
                    }

                    const transcript = entry.transcript;
                    const shape = getRowShape(transcript.type);
                    const gamePlayer = transcript.game_player;
                    const factionId = gamePlayer
                        ? gamePlayer.faction : null;
                    // Faction tint on EVERY row (not just newest), driven
                    // by the actor's faction. Inline style is the only
                    // dynamic-color escape hatch the guide permits.
                    // Message-only rows (system/error) have no faction, so
                    // they fall back to the neutral border from the CSS.
                    const variant = factionId
                        ? FACTION_BS_VARIANT[factionId] : null;
                    const rowStyle = factionRowStyle(factionId, isNewest);
                    const targetFactionId = transcript.to_game_player
                        ? transcript.to_game_player.faction : null;
                    const targetVariant = targetFactionId
                        ? FACTION_BS_VARIANT[targetFactionId] : null;
                    const showSubject = shape.showSubject
                        && transcript.subject != null;
                    const showTarget = shape.showTarget
                        && !!transcript.to_game_player;
                    const outcomes = (shape.showOutcomes
                        && Array.isArray(transcript.outcomes))
                        ? transcript.outcomes : [];
                    const visibleOutcomes = outcomes.slice(0, MAX_OUTCOMES);
                    const overflow = outcomes.length
                        - visibleOutcomes.length;
                    // CC toggle gates the composed caption; rows whose
                    // message IS the payload (chat / system / error)
                    // always show it.
                    const showCaption = shape.messageIsPayload || captionsOn;
                    const rowClass = [
                        'activity-feed-row',
                        'activity-feed-row--' + shape.typeName,
                        isNewest ? 'activity-feed-row--newest' : '',
                    ].filter(Boolean).join(' ');

                    return (
                        <div key={entry.id}
                             className={rowClass}
                             style={rowStyle}
                             data-testid={'activity-feed-row'}>
                            <div className={'activity-feed-row__main'}>
                                <span className={'activity-feed-time'}
                                      data-testid={'activity-feed-time'}>
                                    {formatRelative(entry.receivedAt, now)}
                                </span>
                                {shape.showActor ? (
                                    <span
                                        className={'activity-feed-chip '
                                            + 'activity-feed-chip--actor'}
                                        style={factionChipStyle(variant)}>
                                        <img
                                            className={'activity-feed-actor'}
                                            src={getFactionTextureUrl(
                                                factionId)}
                                            alt={gamePlayer
                                                ? gamePlayer.display_name
                                                : 'player'}
                                            title={gamePlayer
                                                ? gamePlayer.username
                                                : undefined}
                                            data-testid={
                                                'activity-feed-actor'}/>
                                    </span>
                                ) : (
                                    <span
                                        className={
                                            'activity-feed-system-glyph'}
                                        aria-hidden={'true'}
                                        data-testid={
                                            'activity-feed-system-glyph'}>
                                        {transcript.type === ERROR_ID
                                            ? '⚠' : 'ⓘ'}
                                    </span>
                                )}
                                {showSubject && (
                                    <>
                                        <span
                                            className={'activity-feed-arrow'}
                                            aria-hidden={'true'}>
                                            &rarr;
                                        </span>
                                        <span
                                            className={'activity-feed-chip '
                                                + 'activity-feed-chip'
                                                + '--subject'}
                                            style={factionChipStyle(variant)}>
                                            <img
                                                className={
                                                    'activity-feed-subject'}
                                                src={getTextureUrl(
                                                    transcript.subject)}
                                                alt={'subject'}
                                                data-testid={
                                                    'activity-feed-subject'}/>
                                        </span>
                                    </>
                                )}
                                {showTarget && (
                                    <>
                                        <span
                                            className={'activity-feed-arrow'}
                                            aria-hidden={'true'}>
                                            &rarr;
                                        </span>
                                        <span
                                            className={'activity-feed-chip '
                                                + 'activity-feed-chip'
                                                + '--target'}
                                            style={factionChipStyle(
                                                targetVariant)}>
                                            <img
                                                className={
                                                    'activity-feed-target'}
                                                src={getFactionTextureUrl(
                                                    transcript
                                                        .to_game_player
                                                        .faction)}
                                                alt={transcript
                                                    .to_game_player
                                                    .display_name}
                                                data-testid={
                                                    'activity-feed-target'}/>
                                        </span>
                                    </>
                                )}
                                {shape.showOutcomes && (
                                    <span
                                        className={'activity-feed-outcomes'}>
                                        {visibleOutcomes.map((oid, i) => (
                                            <img key={i}
                                                 className={
                                                     'activity-feed-outcome'}
                                                 src={getTextureUrl(oid)}
                                                 alt={`outcome-${oid}`}
                                                 data-testid={
                                                     'activity-feed-outcome'}/>
                                        ))}
                                        {overflow > 0 && (
                                            <span
                                                className={'activity-feed-'
                                                    + 'outcome-more'}
                                                data-testid={'activity-feed-'
                                                    + 'outcome-more'}>
                                                +{overflow}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </div>
                            {showCaption && (
                                <div className={'activity-feed-caption '
                                    + 'activity-feed-caption--'
                                    + shape.typeName}
                                     data-testid={'activity-feed-caption'}>
                                    {captionSegmentsForTranscript(transcript)
                                        .map((seg, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && (
                                                    <span
                                                        className={'activity-'
                                                            + 'feed-caption-'
                                                            + 'arrow'}
                                                        aria-hidden={'true'}>
                                                        {' → '}
                                                    </span>
                                                )}
                                                <span
                                                    className={'activity-feed-'
                                                        + 'caption-segment'}
                                                    style={seg.color
                                                        ? { color: seg.color }
                                                        : undefined}>
                                                    {seg.text}
                                                </span>
                                            </React.Fragment>
                                        ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
